"use client";

import React, { useState, type ReactNode } from "react";
import { Maximize, Minus, Plus, SlidersHorizontal } from "lucide-react";

import {
  Panel,
  useViewport,
  useStore,
  useReactFlow,
  type PanelProps,
} from "@xyflow/react";

import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function ZoomSlider({
  className,
  orientation = "horizontal",
  collapsible = false,
  children,
  ...props
}: Omit<PanelProps, "children" | "style"> & {
  orientation?: "horizontal" | "vertical";
  /** When true, the slider and zoom controls are hidden until the fitView button is clicked. */
  collapsible?: boolean;
  children?: ReactNode;
  style?: React.CSSProperties;
}) {
  const { zoom } = useViewport();
  const { zoomTo, zoomIn, zoomOut, fitView } = useReactFlow();
  const minZoom = useStore((state) => state.minZoom);
  const maxZoom = useStore((state) => state.maxZoom);
  const [expanded, setExpanded] = useState(false);

  const showControls = !collapsible || expanded;

  return (
    <Panel
      className={cn(
        "flex gap-1 rounded-md border border-border bg-background/95 p-1 text-foreground shadow-sm backdrop-blur-sm",
        "dark:border-zinc-700 dark:bg-zinc-950/95 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06)]",
        orientation === "horizontal" ? "flex-row" : "flex-col",
        className,
      )}
      {...props}
    >
      {children && showControls && (
        <>
          <div
            className={cn(
              "flex gap-1",
              orientation === "horizontal" ? "flex-row" : "flex-col",
            )}
          >
            {children}
          </div>
          <div
            className={cn(
              "w-px self-stretch bg-border dark:bg-zinc-700",
              orientation === "vertical" && "h-px w-auto",
            )}
          />
        </>
      )}

      {showControls && (
        <div
          className={cn(
            "flex gap-1",
            orientation === "horizontal" ? "flex-row" : "flex-col-reverse",
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => zoomOut({ duration: 300 })}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Slider
            className={cn(
              orientation === "horizontal" ? "w-[140px]" : "h-[140px]",
            )}
            orientation={orientation}
            value={[zoom]}
            min={minZoom}
            max={maxZoom}
            step={0.01}
            onValueChange={(values) => zoomTo(values[0])}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => zoomIn({ duration: 300 })}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {showControls && (
        <Button
          className={cn(
            "tabular-nums",
            orientation === "horizontal"
              ? "w-[140px] min-w-10"
              : "h-[40px] w-[40px]",
          )}
          variant="ghost"
          onClick={() => zoomTo(1, { duration: 300 })}
        >
          {(100 * zoom).toFixed(0)}%
        </Button>
      )}

      {/* fitView — toujours visible */}
      <Button
        variant="ghost"
        size="icon"
        title="Ajuster la vue"
        onClick={() => fitView({ duration: 300 })}
      >
        <Maximize className="h-4 w-4" />
      </Button>

      {/* Collapse toggle — dernier élément, uniquement en mode collapsible */}
      {collapsible && (
        <Button
          variant="ghost"
          size="icon"
          title={expanded ? "Masquer les contrôles zoom" : "Afficher les contrôles zoom"}
          onClick={() => setExpanded((v) => !v)}
          className={expanded ? "bg-accent" : ""}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      )}
    </Panel>
  );
}
